'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { SOLUCIONES } from '@/data/soluciones'
import { SECTORES } from '@/data/sectores'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import PlayIcon from '@/components/ui/PlayIcon'
import { Calendar, CreditCard, Users, Zap, Globe, BarChart3, Coffee, Briefcase, Building2, HeartPulse, GraduationCap, ShoppingBag, Scissors } from 'lucide-react'
import QuickLeadModal from '@/components/ui/QuickLeadModal'
import ComingSoonModal from '@/components/ui/ComingSoonModal'
import SectorMockup from '@/components/ui/SectorMockup'

const SOLUTION_ICON_MAP: Record<string, any> = {
  Calendar, CreditCard, Users, Zap, Globe, BarChart3,
}

const SECTOR_ICON_MAP: Record<string, any> = {
  Coffee, Briefcase, Building2, HeartPulse, GraduationCap, ShoppingBag, Scissors,
}

const COLOR_MAP: Record<string, any> = {
  blue: { badge: 'bg-blue-100 text-blue-700 border-blue-200', icon: 'text-blue-500', iconBg: 'bg-blue-50', check: 'text-blue-500' },
  green: { badge: 'bg-green-100 text-green-700 border-green-200', icon: 'text-green-500', iconBg: 'bg-green-50', check: 'text-green-500' },
  indigo: { badge: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: 'text-indigo-500', iconBg: 'bg-indigo-50', check: 'text-indigo-500' },
  violet: { badge: 'bg-violet-100 text-violet-700 border-violet-200', icon: 'text-violet-500', iconBg: 'bg-violet-50', check: 'text-violet-500' },
  sky: { badge: 'bg-sky-100 text-sky-700 border-sky-200', icon: 'text-sky-500', iconBg: 'bg-sky-50', check: 'text-sky-500' },
  orange: { badge: 'bg-orange-100 text-orange-700 border-orange-200', icon: 'text-orange-500', iconBg: 'bg-orange-50', check: 'text-orange-500' },
}

const SECTOR_COLOR_PILL: Record<string, string> = {
  hosteleria: 'bg-amber-50 text-amber-700 border-amber-200',
  gestoria: 'bg-blue-50 text-blue-700 border-blue-200',
  construccion: 'bg-orange-50 text-orange-700 border-orange-200',
  clinicas: 'bg-green-50 text-green-700 border-green-200',
  formacion: 'bg-violet-50 text-violet-700 border-violet-200',
  ecommerce: 'bg-pink-50 text-pink-700 border-pink-200',
  peluqueria: 'bg-rose-50 text-rose-700 border-rose-200',
}

/* Map solution color to a suitable SectorMockup color */
const MOCKUP_COLOR_MAP: Record<string, string> = {
  blue: 'blue', green: 'green', indigo: 'indigo', violet: 'violet', sky: 'blue', orange: 'orange',
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' as const, delay },
})

export default function SolucionClient({ slug }: { slug: string }) {
  const solucion = SOLUCIONES.find((s) => s.slug === slug)
  const [modalOpen, setModalOpen] = useState(false)
  const [demoModalOpen, setDemoModalOpen] = useState(false)

  if (!solucion) {
    return <div className="pt-28 pb-20 text-center">Solución no encontrada</div>
  }

  const colors = COLOR_MAP[solucion.color] || COLOR_MAP.blue
  const Icon = SOLUTION_ICON_MAP[solucion.icon] || BarChart3
  const relatedSectors = solucion.sectores
    .map((sSlug) => SECTORES.find((s) => s.slug === sSlug))
    .filter(Boolean)

  const mockupColor = MOCKUP_COLOR_MAP[solucion.color] || 'blue'

  return (
    <>
      <QuickLeadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        context={`${solucion.nombre} — Tengo claro lo que necesito`}
      />
      <ComingSoonModal
        open={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
        productName={solucion.nombre}
      />
      <div className="relative z-10 min-h-screen">
        {/* HERO */}
        <section className="pt-28 pb-16 px-6 lg:px-8">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <motion.div {...fadeUp(0)}>
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border ${colors.badge}`}>
                  <Icon className="w-4 h-4" />
                  {solucion.nombre}
                </span>
              </motion.div>
              <motion.h1 {...fadeUp(0.08)} className="text-4xl sm:text-5xl font-black text-slate-900 leading-tight tracking-tight">
                {solucion.tagline}
              </motion.h1>
              <motion.p {...fadeUp(0.14)} className="text-lg text-slate-500 leading-relaxed max-w-lg">
                {solucion.desc}
              </motion.p>
              <motion.div {...fadeUp(0.20)} className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://calendly.com/innovatheratech/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold text-[15px] shadow-[0_4px_16px_rgba(37,99,235,0.3)] transition-all active:scale-95 bg-blue-600 hover:bg-blue-700"
                >
                  Hablar con el equipo
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setDemoModalOpen(true)}
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-blue-200/80 bg-blue-50/70 text-blue-700 font-semibold text-[15px] hover:bg-blue-100 hover:border-blue-300 transition-all active:scale-95"
                >
                  <PlayIcon className="w-4 h-4 fill-blue-600" />
                  Ir a demo
                </button>
                <button
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-slate-200 bg-white/70 text-slate-700 font-semibold text-[15px] hover:border-slate-300 hover:bg-white transition-all"
                >
                  Tengo claro lo que necesito →
                </button>
              </motion.div>
            </div>

            {/* Mockup panel */}
            <motion.div {...fadeUp(0.15)} className="hidden lg:flex items-center justify-center">
              <SectorMockup
                panelType={solucion.panelType}
                color={mockupColor}
              />
            </motion.div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeUp(0)} className="mb-10">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Funcionalidades</span>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                Todo lo que incluye
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {solucion.features.map((feature, i) => (
                <motion.div key={i} {...fadeUp(i * 0.06)} className="glass-card rounded-2xl p-6 space-y-3">
                  <div className={`w-9 h-9 rounded-xl ${colors.iconBg} flex items-center justify-center`}>
                    <CheckCircle2 className={`w-5 h-5 ${colors.check}`} />
                  </div>
                  <h3 className="text-[15px] font-bold text-slate-800">{feature.title}</h3>
                  <p className="text-[14px] text-slate-500 leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTORES */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeUp(0)} className="mb-10">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Sectores</span>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                ¿Para qué negocios?
              </h2>
              <p className="mt-3 text-slate-500 text-lg max-w-xl">
                Esta solución está pensada para empresas como la tuya.
              </p>
            </motion.div>
            <div className="flex flex-wrap gap-3">
              {relatedSectors.map((sector: any, i: number) => {
                const SectorIcon = SECTOR_ICON_MAP[sector.icon] || Briefcase
                const pillClass = SECTOR_COLOR_PILL[sector.slug] || 'bg-slate-50 text-slate-700 border-slate-200'
                return (
                  <motion.a
                    key={sector.slug}
                    href={`/sectores/${sector.slug}`}
                    {...fadeUp(i * 0.07)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all hover:scale-[1.03] active:scale-95 ${pillClass}`}
                  >
                    <SectorIcon className="w-4 h-4" />
                    {sector.nombre}
                  </motion.a>
                )
              })}
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div {...fadeUp(0)} className="glass-card rounded-3xl p-10 sm:p-14 shadow-xl space-y-6">
              <div className={`w-14 h-14 rounded-2xl ${colors.iconBg} flex items-center justify-center mx-auto`}>
                <Icon className={`w-7 h-7 ${colors.icon}`} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                Empieza hoy
              </h2>
              <p className="text-slate-500 text-lg max-w-md mx-auto leading-relaxed">
                Reserva una llamada de 30 minutos y te explicamos cómo implementamos {solucion.nombre.toLowerCase()} en tu negocio.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://calendly.com/innovatheratech/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-white font-bold text-[15px] shadow-[0_4px_20px_rgba(37,99,235,0.3)] transition-all active:scale-95 bg-blue-600 hover:bg-blue-700"
                >
                  Agenda tu llamada gratis
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setDemoModalOpen(true)}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl border border-blue-200/80 bg-blue-50/70 text-blue-700 font-semibold text-[15px] hover:bg-blue-100 hover:border-blue-300 transition-all active:scale-95"
                >
                  <PlayIcon className="w-4 h-4 fill-blue-600" />
                  Ir a demo
                </button>
                <button
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl border border-slate-200 bg-white/70 text-slate-700 font-semibold text-[15px] hover:border-slate-300 hover:bg-white transition-all"
                >
                  Tengo claro lo que necesito →
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}
